/**
 * File Transfer Service
 * Handles file upload/download via SignalR hub with chunked transfer and progress tracking.
 * Compatible with RemoteDesktop.Shared/Models/FileTransferModels.cs
 */

import { SignalRService } from './SignalRService';

export interface FileTransferProgress {
    transferId: string;
    fileName: string;
    direction: 'upload' | 'download';
    bytesTransferred: number;
    totalBytes: number;
    percent: number;
    status: 'pending' | 'transferring' | 'completed' | 'failed' | 'cancelled';
    error?: string;
}

export interface FileTransferEvents {
    onProgress?: (progress: FileTransferProgress) => void;
    onComplete?: (transferId: string, fileName: string) => void;
    onError?: (transferId: string, error: string) => void;
}

export interface RemoteFileEntry {
    name: string;
    isDirectory: boolean;
    size: number;
    lastModified: string;
    fullPath: string;
}

const CHUNK_SIZE = 65536; // 64 KB — matches Constants.FileTransferChunkSize

export class FileTransferService {
    private signalR: SignalRService;
    private sessionId: string | null = null;
    private events: FileTransferEvents;
    private activeTransfers = new Map<string, FileTransferProgress>();
    // WC-3: Download chunk accumulator
    private downloadChunks = new Map<string, Uint8Array[]>();

    /** External progress listener (e.g., FileTransferPanel UI updates) */
    public onProgressExternal?: (progress: FileTransferProgress) => void;

    constructor(signalR: SignalRService, events: FileTransferEvents = {}) {
        this.signalR = signalR;
        this.events = events;
    }

    private emitProgress(progress: FileTransferProgress): void {
        this.events.onProgress?.(progress);
        this.onProgressExternal?.(progress);
    }

    setSessionId(sessionId: string | null): void {
        this.sessionId = sessionId;
    }

    /** Upload a file from the browser to the remote agent */
    async uploadFile(file: File, remotePath: string): Promise<string> {
        if (!this.sessionId) throw new Error('No active session');

        const transferId = crypto.randomUUID();
        const progress: FileTransferProgress = {
            transferId,
            fileName: file.name,
            direction: 'upload',
            bytesTransferred: 0,
            totalBytes: file.size,
            percent: 0,
            status: 'pending'
        };
        this.activeTransfers.set(transferId, progress);

        try {
            // Request file transfer
            const request = {
                transferId,
                fileName: file.name,
                fileSize: file.size,
                destinationPath: remotePath,
                direction: 0, // Upload (Viewer → Agent)
                checksum: ''
            };

            const response = await this.signalR.invoke(
                'RequestFileTransfer', this.sessionId, request
            );

            if (!response?.accepted) {
                throw new Error(response?.reason || 'Transfer rejected');
            }

            progress.status = 'transferring';
            this.emitProgress(progress);

            // Send file in chunks
            const buffer = await file.arrayBuffer();
            const totalChunks = Math.ceil(buffer.byteLength / CHUNK_SIZE);

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, buffer.byteLength);
                const chunkData = new Uint8Array(buffer.slice(start, end));

                const chunk = {
                    transferId,
                    chunkIndex: i,
                    totalChunks,
                    data: Array.from(chunkData), // SignalR JSON sends as number array
                    isLast: i === totalChunks - 1
                };

                await this.signalR.invoke(
                    'SendFileChunk', this.sessionId, chunk
                );

                progress.bytesTransferred = end;
                progress.percent = Math.round((end / file.size) * 100);
                this.emitProgress(progress);
            }

            progress.status = 'completed';
            progress.percent = 100;
            this.emitProgress(progress);
            this.activeTransfers.delete(transferId); // M2: cleanup after upload
            this.events.onComplete?.(transferId, file.name);
            return transferId;
        } catch (error: any) {
            progress.status = 'failed';
            progress.error = error.message;
            this.emitProgress(progress);
            this.activeTransfers.delete(transferId); // M2: cleanup on failure
            this.events.onError?.(transferId, error.message);
            throw error;
        }
    }

    /** Request download of a remote file */
    async requestDownload(remotePath: string, fileName: string): Promise<string> {
        if (!this.sessionId) throw new Error('No active session');

        const transferId = crypto.randomUUID();
        const request = {
            transferId,
            fileName,
            fileSize: 0,
            sourcePath: remotePath,
            direction: 1, // Download (Agent → Viewer)
            checksum: ''
        };

        await this.signalR.invoke(
            'RequestFileTransfer', this.sessionId, request
        );

        const progress: FileTransferProgress = {
            transferId,
            fileName,
            direction: 'download',
            bytesTransferred: 0,
            totalBytes: 0,
            percent: 0,
            status: 'pending'
        };
        this.activeTransfers.set(transferId, progress);
        this.emitProgress(progress);

        return transferId;
    }

    /** Browse a remote directory */
    async browseDirectory(path: string): Promise<RemoteFileEntry[]> {
        if (!this.sessionId) throw new Error('No active session');

        const request = { path, sessionId: this.sessionId };
        const response = await this.signalR.invoke(
            'BrowseDirectory', this.sessionId, request
        );

        return response?.entries || [];
    }

    /** Cancel an active transfer */
    async cancelTransfer(transferId: string): Promise<void> {
        if (!this.sessionId) return;
        const progress = this.activeTransfers.get(transferId);
        if (progress) {
            progress.status = 'cancelled';
            this.emitProgress(progress);
        }
        // M2: Clean up maps
        this.activeTransfers.delete(transferId);
        this.downloadChunks.delete(transferId);
        await this.signalR.invoke(
            'CancelFileTransfer', this.sessionId, transferId
        );
    }

    /** Get all active transfers */
    getActiveTransfers(): FileTransferProgress[] {
        return Array.from(this.activeTransfers.values());
    }

    // WC-3: Handlers for server-pushed file transfer events

    /** Handle incoming file chunk (download from agent) */
    handleChunkReceived(chunk: { transferId: string; chunkIndex: number; totalChunks: number; data: Uint8Array; isLast: boolean }): void {
        const progress = this.activeTransfers.get(chunk.transferId);
        if (!progress) return;

        // Accumulate chunks
        if (!this.downloadChunks.has(chunk.transferId)) {
            this.downloadChunks.set(chunk.transferId, []);
        }
        this.downloadChunks.get(chunk.transferId)!.push(chunk.data);

        progress.status = 'transferring';
        progress.bytesTransferred += chunk.data.length;
        if (progress.totalBytes > 0) {
            progress.percent = Math.round((progress.bytesTransferred / progress.totalBytes) * 100);
        }
        this.emitProgress({ ...progress });

        if (chunk.isLast) {
            this.finalizeDownload(chunk.transferId);
        }
    }

    /** Handle transfer progress update from server */
    handleTransferProgress(serverProgress: { transferId: string; bytesTransferred: number; totalBytes: number; percent: number; status: string }): void {
        const progress = this.activeTransfers.get(serverProgress.transferId);
        if (!progress) return;

        progress.bytesTransferred = serverProgress.bytesTransferred;
        progress.totalBytes = serverProgress.totalBytes;
        progress.percent = serverProgress.percent;
        this.emitProgress({ ...progress });
    }

    /** Handle transfer completed notification from server */
    handleTransferComplete(complete: { transferId: string; fileName: string; fileSize: number; checksum: string }): void {
        // M3: If we have accumulated chunks, trigger the browser download
        if (this.downloadChunks.has(complete.transferId)) {
            this.finalizeDownload(complete.transferId);
            return; // finalizeDownload already fires onComplete
        }
        // No chunks — upload completion or already finalized via isLast
        const progress = this.activeTransfers.get(complete.transferId);
        if (progress) {
            progress.status = 'completed';
            progress.percent = 100;
            this.emitProgress({ ...progress });
            this.activeTransfers.delete(complete.transferId); // M2: cleanup
            this.events.onComplete?.(complete.transferId, complete.fileName);
        }
        // If no progress entry either, this is a duplicate/late completion — ignore
    }

    /** Handle browse directory response from server */
    handleDirectoryResponse(response: { path: string; entries: RemoteFileEntry[]; error?: string }): RemoteFileEntry[] {
        return response?.entries || [];
    }

    private finalizeDownload(transferId: string): void {
        const chunks = this.downloadChunks.get(transferId);
        const progress = this.activeTransfers.get(transferId);
        if (!chunks || !progress) return;

        // Combine chunks into blob and trigger browser download
        // Blob constructor accepts Uint8Array[] without holding all data in separate arrays
        const blob = new Blob(chunks as unknown as BlobPart[]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = progress.fileName;
        a.click();
        // Delay revoke to let the browser start the download (Firefox needs this)
        setTimeout(() => URL.revokeObjectURL(url), 30000);

        // Free chunk memory immediately after creating blob
        this.downloadChunks.delete(transferId);
        progress.status = 'completed';
        progress.percent = 100;
        this.emitProgress({ ...progress });
        this.activeTransfers.delete(transferId); // M2: prevent memory leak
        this.events.onComplete?.(transferId, progress.fileName);
    }

    dispose(): void {
        this.activeTransfers.clear();
        this.downloadChunks.clear();
    }
}
